import { Grid, Paper, Typography } from '@material-ui/core';
import moment from 'moment';
import React, { ReactElement } from 'react';
import { Bar, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { DashboardDateToggles } from '~/src/dash/components/dashboard/dashboard-date-toggles';
import { DashboardTooltipV2 } from '~/src/dash/components/dashboard/dashboard-tooltip-v2';
import { DashedBar } from '~/src/dash/components/dashboard/dashed-bar';
import { publicaColors } from '~/src/dash/utils/colors';
import { DateGroup, DateGroupDays } from '~/src/dash/utils/date';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { getTransparentColor } from '~/src/dash/utils/ui';
import {
  IIASDashboardDisplayedCardKeyAndColor,
  IIASDashboardMetricInfo,
} from '~/src/dash/views/ias-publisher/IASDashboard/common/types';

const integerAxis = (
  <YAxis
    yAxisId='integer'
    orientation='right'
    scale='auto'
    tick={{ fontFamily: 'inherit' }}
    domain={[0, 'auto']}
    unit={''}
    tickFormatter={(value: number) => value.toLocaleString()}
  />
);
const rateAxis = (
  <YAxis
    yAxisId='rate'
    orientation='left'
    scale='auto'
    tick={{ fontFamily: 'inherit' }}
    domain={[0, 'auto']}
    unit={''}
    tickFormatter={(value: number) => value.toLocaleString()}
  />
);

const yAxes = [integerAxis, rateAxis];

// Notes: we have to create an array of JSX due to recharts limitations.
const generateChart = (
  metrics: Record<string, IIASDashboardMetricInfo>,
  activeMetrics: Record<string, boolean>,
  isComparedToEnabled: boolean,
  isDataLoaded: boolean,
  displayedCardsKeysAndColors: IIASDashboardDisplayedCardKeyAndColor[],
  areCardsSelectable: boolean | undefined,
) => {
  const dataPoints = [] as ReactElement[];
  const metricsArray = Object.values(metrics);
  const displayedCardsKeys = [];
  for (const keyAndColor of displayedCardsKeysAndColors) {
    displayedCardsKeys.push(keyAndColor.dataKey);
  }

  for (const metric of metricsArray) {
    const { chartType, dataKey, compareKey, color, yAxisID } = metric;
    let cardIndex;
    let selectableCardColor = publicaColors.gray;
    if (areCardsSelectable) {
      cardIndex = displayedCardsKeys.indexOf(dataKey);
      selectableCardColor =
        cardIndex && cardIndex !== -1
          ? displayedCardsKeysAndColors[cardIndex].color
          : publicaColors.gray;
    }
    if (activeMetrics[dataKey]) {
      if (chartType === 'line') {
        dataPoints.push(
          <Line
            hide={!isDataLoaded}
            dot={false}
            type='monotone'
            dataKey={dataKey}
            stroke={areCardsSelectable ? selectableCardColor : color || publicaColors.gray}
            yAxisId={yAxisID}
            strokeWidth={4}
          />,
        );
        if (isComparedToEnabled) {
          dataPoints.push(
            <Line
              hide={!isDataLoaded}
              dot={false}
              type='monotone'
              dataKey={compareKey}
              stroke={getTransparentColor(
                areCardsSelectable ? selectableCardColor : color || publicaColors.gray,
                '66',
              )}
              yAxisId={yAxisID}
              strokeWidth={4}
              strokeDasharray='5 5'
            />,
          );
        }
      } else if (chartType === 'bar') {
        dataPoints.push(
          <Bar
            hide={!isDataLoaded}
            barSize={50}
            dataKey={dataKey}
            fill={color || publicaColors.gray}
            yAxisId={yAxisID}
          />,
        );
        if (isComparedToEnabled) {
          dataPoints.push(
            <Bar
              hide={!isDataLoaded}
              barSize={50}
              dataKey={compareKey}
              fill={getTransparentColor(
                areCardsSelectable ? selectableCardColor : color || publicaColors.gray,
                '66',
              )}
              yAxisId={yAxisID}
              shape={
                <DashedBar
                  fill={getTransparentColor(
                    areCardsSelectable ? selectableCardColor : color || publicaColors.gray,
                    '66',
                  )}
                  x={0}
                  y={0}
                  width={0}
                  height={0}
                  value={0}
                />
              }
            />,
          );
        }
      }
    }
  }
  return dataPoints;
};

interface IIASDashboardChartProps {
  classes: { [key: string]: string };
  data: any;
  loadState: LoadingState;
  chartLoadState: number;
  compChartLoadState: number;
  handleDensityRangeChange: (densityRangeAgg: DateGroup) => void;
  calStartDate: moment.Moment;
  compStartDate: moment.Moment;
  calEndDate: moment.Moment;
  compEndDate: moment.Moment;
  densityRangeAgg: DateGroup;
  isComparedToEnabled: boolean;
  metrics: Record<string, IIASDashboardMetricInfo>;
  activeMetrics: Record<string, boolean>;
  displayedCardsKeysAndColors: IIASDashboardDisplayedCardKeyAndColor[];
  areCardsSelectable: boolean | undefined;
}

export const IASDashboardChart = ({
  classes,
  data,
  chartLoadState,
  compChartLoadState,
  densityRangeAgg,
  handleDensityRangeChange,
  calStartDate,
  calEndDate,
  isComparedToEnabled,
  metrics,
  activeMetrics,
  displayedCardsKeysAndColors,
  areCardsSelectable,
}: IIASDashboardChartProps) => {
  const isDataLoaded = isComparedToEnabled
    ? chartLoadState === LoadingStateHasData && compChartLoadState === LoadingStateHasData
    : chartLoadState === LoadingStateHasData;
  const hasNoData = isComparedToEnabled
    ? chartLoadState === LoadingStateNoData && compChartLoadState === LoadingStateNoData
    : chartLoadState === LoadingStateNoData;
  return (
    <div>
      <Paper className={classes.paper}>
        <Grid container alignItems='center' spacing={1}>
          <Grid xs={10} item></Grid>
          <Grid item xs={2} style={{ width: '100%' }}>
            <DashboardDateToggles
              classes={classes}
              densityRange={densityRangeAgg || DateGroupDays}
              handleDensityRangeChange={handleDensityRangeChange}
              calStartDate={calStartDate || moment()}
              calEndDate={calEndDate || moment()}
              hourAggRangeDisabled
            />
          </Grid>
          {isDataLoaded ? (
            <ResponsiveContainer width='99%' height={400}>
              <ComposedChart data={data}>
                {metrics &&
                  activeMetrics &&
                  generateChart(
                    metrics,
                    activeMetrics,
                    isComparedToEnabled,
                    isDataLoaded,
                    displayedCardsKeysAndColors,
                    areCardsSelectable,
                  )}
                <XAxis
                  dataKey={'Date'}
                  angle={-45}
                  textAnchor='end'
                  height={100}
                  interval={0}
                  tick={{ fontSize: 12 }}
                />
                {yAxes}
                <Tooltip
                  wrapperStyle={{ width: 300, zIndex: 999 }}
                  content={
                    <DashboardTooltipV2
                      densityRange={densityRangeAgg || DateGroupDays}
                      loaded={isDataLoaded}
                      currencySymbol={''}
                    />
                  }
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <Grid container alignItems='center' direction='column'>
              <Grid
                item
                style={{
                  width: '99%',
                  height: 400,
                  textAlign: 'center',
                }}
              >
                <Typography style={{ marginTop: 150 }}>
                  {hasNoData
                    ? 'No data to display for the selected period or filters'
                    : 'Loading query...'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Paper>
    </div>
  );
};
